import React from 'react';
import { useFields } from './fields.js';
import { useTables } from './tables.js';
import { useGlobalSettings } from './global-settings.js';
import { usePicklists } from './picklists.js';
import { useUIComponents } from './ui-components.js';
import { useRelationships } from './relationships.js';
import { useRoutes } from './routes.js';
import { useConstraints } from './constraints.js';
import { useGridPresets } from './grid-presets.js';
import { useClientSideData } from './client-side-data.js';
import { useReportMetadata } from './report-metadata.js';
import { useDashboardMetadata } from './dashboard-metadata.js';
import { useGmailMetadata } from './gmail.js';
import { useAuth } from '../useAuth.js';
import type {
  ProcessedFieldMetadata,
  ProcessedTableMetadata,
  ProcessedRelationshipMetadata,
  ProcessedPicklistMetadata,
  ProcessedConstraintMetadata,
  GlobalSettings,
  UIComponentMetadata,
  RouteMetadata,
  GridPresetMetadata,
  GmailMetadata
} from '../../types/metadata.js';
import type { ReportMetadata } from '../../types/reporting.js';
import type { DashboardMetadata } from '../../types/dashboard.js';
import type { BaseRecord } from '../../types/api.js';

// Comprehensive metadata result type
export interface ProcessedMetadata {
  routeData: RouteMetadata | null;
  fieldData: ProcessedFieldMetadata | null;
  tableData: ProcessedTableMetadata | null;
  globalSettingsData: GlobalSettings | null;
  clientSideSelectTables: {
    flattened: Record<string, BaseRecord>;
    [tableName: string]: any;
  };
  picklistData: ProcessedPicklistMetadata | null;
  uiComponents: UIComponentMetadata[] | null;
  relationshipLookupData: ProcessedRelationshipMetadata | null;
  constraintData: ProcessedConstraintMetadata | null;
  gridPresetData: GridPresetMetadata | null;
  reportData: ReportMetadata | null;
  dashboardData: DashboardMetadata | null;
  gmailData: GmailMetadata | null;
}

// Hook result interface
interface UseMetadataResult {
  data: ProcessedMetadata | null;
  error: Error | null;
  isLoading: boolean;
}

/**
 * Central metadata hook that combines all metadata sources into a single comprehensive object.
 * This is the primary interface for accessing all CRM metadata throughout the application.
 * 
 * @returns Combined metadata with loading and error states
 */
export const useMetadata = (): UseMetadataResult => {
    // All hooks must be called in the same order every time
    const { isAdmin } = useAuth();
    const { data: fieldData, error: fieldError, isLoading: fieldIsLoading } = useFields({enabled:true});
    const { data: tableData, error: tableError, isLoading: tableIsLoading } = useTables({enabled:true});
    const { data: globalSettingsData, error: globalSettingsError, isLoading: globalSettingsIsLoading } = useGlobalSettings({enabled:true});
    const { data: picklistData, error: picklistError, isLoading: picklistIsLoading } = usePicklists({enabled:true});
    const { data: uiComponents, error: uiComponentError, isLoading: uiComponentIsLoading } = useUIComponents({enabled:true});
    const { data: relData, error: relError, isLoading: relIsLoading } = useRelationships({enabled:true});
    const { data: routeData, error: routeError, isLoading: routeIsLoading } = useRoutes({enabled:true});
    const { data: constraintData, error: constraintError, isLoading: constraintIsLoading } = useConstraints({enabled:true});
    const { data: gridPresetData, error: gridPresetError, isLoading: gridPresetIsLoading } = useGridPresets({enabled:true});
    const { data: reportData, error: reportError, isLoading: reportIsLoading } = useReportMetadata({enabled:true});
    const { data: dashboardData, error: dashboardError, isLoading: dashboardIsLoading } = useDashboardMetadata({enabled:true});
    // Gmail provider/mailbox rows are administrative configuration. Normal
    // users access the scoped email archive, not this global metadata query.
    const { data: gmailData, error: gmailError, isLoading: gmailIsLoading } = useGmailMetadata({enabled:isAdmin});
    
    // react query returns a new object every time, so we need to use a stable reference to prevent infinite re-renders caused
    // by the useClientSideData hook.

    const tableDataCacheKey = React.useMemo(() => {
        if (!tableData) return [''];
        return [tableData.asList.map((a: any) => a.updated_at).join('')];
    }, [tableData]);
    
    const globalSettingsCacheKey: string[] = [globalSettingsData?.updated_at || ''];

    const cachedTableData = React.useMemo(() => tableData, [tableDataCacheKey[0]]);
    const cachedGlobalSettingsData = React.useMemo(() => globalSettingsData, globalSettingsCacheKey);
    
    const { data: clientSideData, isLoading: clientSideLoading, error: clientSideError } = useClientSideData(cachedTableData, cachedGlobalSettingsData, isAdmin);
    
    const isLoading = fieldIsLoading || tableIsLoading || globalSettingsIsLoading || picklistIsLoading || uiComponentIsLoading || relIsLoading || routeIsLoading || constraintIsLoading || gridPresetIsLoading || reportIsLoading || dashboardIsLoading || (isAdmin && gmailIsLoading) ||
    // userLoading ||
    clientSideLoading;

    const errors: any[] = [fieldError, tableError, globalSettingsError, picklistError, uiComponentError, relError, routeError, constraintError, gridPresetError, reportError, dashboardError, ...(isAdmin ? [gmailError] : []), clientSideError];
    const errorMessage = errors
      .filter(Boolean)
      .map((err: any) => err?.message || String(err))
      .join(', ');

    // Memoized on the message so a stable error state doesn't produce a new
    // Error instance (and therefore a new result object) on every render.
    const error: Error | null = React.useMemo(
        () => (errorMessage ? new Error(errorMessage) : null),
        [errorMessage]
    );

    // Build client-side select tables object from dynamically loaded data.
    // Normalize client-side select tables to always expose a `label` field.
    //
    // Memoized: this object (and `data` below) must keep a stable identity
    // between renders. Consumers — most importantly TableGrid — memoize their
    // AG Grid `serverSideDatasource` and `columnDefs` on the metadata object,
    // and a new reference makes AG Grid destroy the datasource and purge the
    // row store, which flashes the grid empty on every unrelated re-render.
    const clientSideSelectTables = React.useMemo(() => {
        const result: {
            flattened: Record<string, BaseRecord>;
            [tableName: string]: any;
        } = { flattened: {} };

        // Build a lookup of default label fields per target table from relationship metadata
        const targetLabelFieldByTable: Record<string, string> = {};
        relData?.asList?.forEach((rel: any) => {
            if (rel?.target_table_name && rel?.target_label_field && !targetLabelFieldByTable[rel.target_table_name]) {
                targetLabelFieldByTable[rel.target_table_name] = rel.target_label_field;
            }
        });

        if (clientSideData) {
            Object.keys(clientSideData).forEach(tableName => {
                const tableRecords = clientSideData[tableName];
                if (!tableRecords) {
                    return;
                }

                const labelField = targetLabelFieldByTable[tableName];
                const normalizedRecords = Array.isArray(tableRecords)
                    ? tableRecords.map((row: BaseRecord) => {
                        if (row && (row as any).label != null) {
                            return row;
                        }
                        const labelValue = labelField && (row as any)[labelField] != null
                            ? (row as any)[labelField]
                            : row?.id;
                        return { ...row, label: labelValue };
                    })
                    : tableRecords;

                result[tableName] = normalizedRecords;

                // Add to flattened lookup for optimization
                if (Array.isArray(normalizedRecords)) {
                    normalizedRecords.forEach((row: BaseRecord) => {
                        result.flattened[`${tableName}_${row.id}`] = row;
                    });
                }
            });
        }

        return result;
    }, [clientSideData, relData]);

    const data: ProcessedMetadata = React.useMemo(() => ({
        routeData,
        fieldData,
        tableData,
        globalSettingsData,
        clientSideSelectTables,
        picklistData,
        uiComponents,
        relationshipLookupData: relData,
        constraintData,
        gridPresetData,
        reportData: reportData ?? null,
        dashboardData: dashboardData ?? null,
        gmailData: gmailData ?? null,
    }), [
        routeData,
        fieldData,
        tableData,
        globalSettingsData,
        clientSideSelectTables,
        picklistData,
        uiComponents,
        relData,
        constraintData,
        gridPresetData,
        reportData,
        dashboardData,
        gmailData,
    ]);

    // The result wrapper is memoized too — MetadataProvider memoizes its context
    // value on this object's identity.
    return React.useMemo<UseMetadataResult>(
        () => (isLoading || error
            ? { data: null, error, isLoading }
            : { data, error: null, isLoading: false }),
        [isLoading, error, data]
    );
};
